import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router"

import { ApiError } from "../../../../../core/api/errorTypes"
import { fetchUserWallets } from "../../settings/wallets/refill/refillApi"
import { SelectWalletDialog } from "../../settings/wallets/refill/components/SelectWalletDialog"
import { MakePurchaseDialog } from "../../settings/wallets/refill/components/MakePurchaseDialog"
import { UserWalletsRefillState } from "../../settings/wallets/refill/pages/UserWalletsRefill"
import { UserWallet } from "../../settings/wallets/refill/refillTypes"
import { useOrderDialogUrlState } from "../../../../../shared/components/createOrder/hooks/useOrderDialogUrlState"
import { getUserDetails, UserQuery } from "../../../../users/userApi"
import { CreateOrderType } from "../../../../../shared/components/createOrder/helpers"
import { TakeawayOrderHost } from "../../../../../shared/components/createOrder/components/takeaway/TakeawayOrderHost"
import { selectUserId } from "../../../../login/loginSelectors"
import { useAppSelector } from "../../../../store/storeHooks"
import {
    TopBarNewButtonMenu,
    UserActionsMenuItemConfig,
} from "../../../../../shared/components/topBarNewButtonMenu/TopBarNewButtonMenu"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { getRefillWalletRoute } from "../../../../../constants/routes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { moduleItemFactories } from "./moduleItemFactories"
import { useMatchEnvironment } from "../../../../environment/envHooks"
import { EnvironmentType } from "../../../../environment/envTypes"
import { ModuleName } from "../../../../users/userTypes"

export function UserCreateNewButton() {
    const userId = useAppSelector(selectUserId)
    const { openDialog } = useOrderDialogUrlState()
    const navigate = useNavigate()
    const isAppPaymentEnabled = useMatchEnvironment([EnvironmentType.Local, EnvironmentType.Test])
    const isRefillWalletEnabled = !useMatchEnvironment([EnvironmentType.QA, EnvironmentType.Prod])

    const [openSelectWalletDialog, setOpenSelectWalletDialog] = useState(false)
    const [openMakePurchaseDialog, setOpenMakePurchaseDialog] = useState(false)
    const [openTakeawayDialog, setOpenTakeawayDialog] = useState(false)
    const [userWallets, setUserWallets] = useState<Array<UserWallet> | null>(null)
    const [walletErrorMessage, setWalletErrorMessage] = useState<string | null>(null)

    const { mutateAsync } = useApiMutation(fetchUserWallets, {
        async onSuccess(_, data) {
            setUserWallets(data.wallets)
            setWalletErrorMessage(null)
        },
        async onError(_, error) {
            console.log("app: error", error)
            const message = error instanceof ApiError ? error.displayMessage : null
            setWalletErrorMessage(message)
            setOpenSelectWalletDialog(!!message)
        },
    })

    const { data: userDetails } = useApiQuery(getUserDetails, {
        queryName: UserQuery.USER_DETAILS,
        dependencies: {
            userId: userId ?? 0,
        },
    })

    const handleCreateOrder = useCallback(
        (type: CreateOrderType) => {
            openDialog({
                activeOrderTab: type,
            })
        },
        [openDialog],
    )

    const handleRefillWallet = useCallback(async () => {
        const response = await mutateAsync({ userId: userId ?? 0 })

        if (response.wallets?.length === 1) {
            const state: UserWalletsRefillState = {
                wallet: response.wallets[0],
            }
            navigate(getRefillWalletRoute(response.wallets[0].acquirer.acquirerSetupId, response.wallets[0]?.id), {
                state,
            })
        } else {
            setOpenSelectWalletDialog(true)
        }
    }, [mutateAsync, userId, navigate])

    const handleOpenMakePurchase = useCallback(() => {
        setOpenMakePurchaseDialog(true)
    }, [])

    const handleOpenTakeaway = useCallback(() => {
        setOpenTakeawayDialog(true)
    }, [])

    const handleCloseTakeaway = useCallback(() => {
        setOpenTakeawayDialog(false)
    }, [])

    const handleCloseSelectWallet = useCallback(() => {
        setOpenSelectWalletDialog(false)
        setWalletErrorMessage(null)
    }, [])

    const handleSelectWallet = useCallback(
        (wallet: UserWallet) => {
            setOpenSelectWalletDialog(false)
            const state: UserWalletsRefillState = { wallet }
            navigate(getRefillWalletRoute(wallet.acquirer.acquirerSetupId, wallet.id), { state })
        },
        [navigate],
    )

    const handleCloseMakePurchase = useCallback(() => {
        setOpenMakePurchaseDialog(false)
    }, [])

    const activeModules = useMemo(
        () => new Set(userDetails?.user?.modules?.filter((m) => m.isActive)?.map((m) => m.name)),
        [userDetails],
    )

    const items: UserActionsMenuItemConfig[] = useMemo(
        () =>
            moduleItemFactories({
                handleCreateOrder,
                handleOpenTakeaway,
                handleOpenMakePurchase,
                handleRefillWallet,
            })
                .filter((factory) => activeModules.has(factory.moduleName))
                .filter((factory) => factory.moduleName !== ModuleName.APP_PAYMENT || isAppPaymentEnabled)
                .filter((factory) => factory.moduleName !== ModuleName.REFILL_WALLET || isRefillWalletEnabled)
                .map((factory) => factory.create()),
        [
            handleCreateOrder,
            handleOpenTakeaway,
            handleOpenMakePurchase,
            handleRefillWallet,
            activeModules,
            isAppPaymentEnabled,
            isRefillWalletEnabled,
        ],
    )

    return (
        <>
            <TopBarNewButtonMenu items={items} />

            {/*hide it for now*/}
            {/* {false && (
                <>
                    {activeModules.size > 1 && (
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <MenuItem className={classes.menuItem} onClick={handleMenuClose}>
                            <Box className={cx(classes.iconBox, classes.iconSlate)}>
                                <FontAwesomeIcon icon={faShieldKeyhole} />
                            </Box>
                            <Box>
                                <LocalizedStrict id="user-create-new-menu-order-for-another-user-title">
                                    <Typography variant="subtitle1">Order on behalf of another user</Typography>
                                </LocalizedStrict>
                                <LocalizedStrict id="user-create-new-menu-order-for-another-user-subtitle">
                                    <Typography variant="body2" color="textSecondary">
                                        Create a resource booking or meeting service order on behalf of another user
                                    </Typography>
                                </LocalizedStrict>
                            </Box>
                        </MenuItem>
                    </Grid>
                </>
            )} */}

            {openTakeawayDialog && <TakeawayOrderHost open={openTakeawayDialog} onClose={handleCloseTakeaway} />}

            {openSelectWalletDialog && (
                <SelectWalletDialog
                    open={openSelectWalletDialog}
                    onClose={handleCloseSelectWallet}
                    onSelect={handleSelectWallet}
                    userWallets={userWallets}
                    errorMessage={walletErrorMessage}
                />
            )}

            {openMakePurchaseDialog && (
                <MakePurchaseDialog open={openMakePurchaseDialog} onClose={handleCloseMakePurchase} />
            )}
        </>
    )
}
